/**
 * Cortex domain event schemas.
 *
 * Typed payload validation for Cortex coordination Mycelium events.
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module cortex.events
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

// ── Event schemas ───────────────────────────────────────────────────────

export const CortexWorkRequestedEvent = BackboneEventSchema.extend({
  domain: z.literal('cortex'),
  type: z.literal('cortex_work_requested'),
  payload: z.object({
    intent: z.string(),
    triggerDomain: z.string().optional(),
    triggerType: z.string().optional(),
    triggerEventId: z.string().optional(),
    triggerContext: z.record(z.unknown()).optional(),
    requestedAt: z.string().optional(),
    priority: z.string().optional(),
    source: z.string().optional(),
  }),
})

/**
 * External agent surface → Cortex: Request creation of a Cortex session with injected context.
 * Use case: An external orchestrator detects an anomaly and wants to alert the user with full context.
 */
export const CortexSessionRequestedEvent = BackboneEventSchema.extend({
  domain: z.literal('cortex'),
  type: z.literal('cortex_session_requested'),
  payload: z.object({
    userId: z.string(),
    orgId: z.string().optional(),
    title: z.string(),
    initialMessage: z.string(),
    context: z.record(z.unknown()).optional(),
    priority: z.enum(['high', 'normal', 'low']).optional(),
    /** Generic agent bridge metadata — any surface can inject context here. */
    agentBridgeMetadata: z.object({
      taskId: z.string().optional(),
      sourceChannel: z.string().optional(),
      escalationReason: z.string().optional(),
      agentSurface: z.string().optional(),
    }).optional(),
    source: z.string(),
  }),
})

// ── Generic Agent Action Events (surface-agnostic) ──────────────────────

/**
 * Cortex → Agent: Request system-level action that requires external orchestrator.
 * Generic version of the legacy agent action request event.
 */
export const AgentActionRequestedEvent = BackboneEventSchema.extend({
  domain: z.literal('cortex'),
  type: z.literal('agent_action_requested'),
  payload: z.object({
    action: z.string(),
    sessionId: z.string(),
    userId: z.string(),
    orgId: z.string().optional(),
    /** Which agent surface should handle this action (e.g. 'openclaw', 'hermes'). */
    agentSurface: z.string().optional(),
    actionParams: z.record(z.unknown()).optional(),
    escalationContext: z.object({
      userQuery: z.string().optional(),
      insufficientContext: z.string().optional(),
      attemptedActions: z.array(z.string()).optional(),
    }).optional(),
    source: z.string(),
  }),
})

/**
 * Agent → Cortex: Return results from system-level action.
 * Generic version of the legacy agent action completed event.
 */
export const AgentActionCompletedEvent = BackboneEventSchema.extend({
  domain: z.literal('cortex'),
  type: z.literal('agent_action_completed'),
  payload: z.object({
    sessionId: z.string(),
    action: z.string(),
    success: z.boolean(),
    /** Which agent surface completed the action. */
    agentSurface: z.string().optional(),
    result: z.record(z.unknown()).optional(),
    errorMessage: z.string().optional(),
    insights: z.array(z.string()).optional(),
    actionItems: z.array(z.object({
      title: z.string(),
      description: z.string(),
      priority: z.string().optional(),
    })).optional(),
    source: z.string(),
  }),
})

/**
 * Cortex → Agent: Notify of session lifecycle events.
 * Generic version; any agent surface can subscribe.
 */
export const CortexSessionEventEvent = BackboneEventSchema.extend({
  domain: z.literal('cortex'),
  type: z.literal('cortex_session_event'),
  payload: z.object({
    sessionId: z.string(),
    eventType: z.enum(['created', 'resumed', 'completed', 'escalated', 'error']),
    userId: z.string(),
    orgId: z.string().optional(),
    /** Which agent surface this event targets (null = broadcast). */
    agentSurface: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
    source: z.string(),
  }),
})

// ── All cortex events ───────────────────────────────────────────────────

export const CortexDomainEvents = [
  CortexWorkRequestedEvent,
  CortexSessionRequestedEvent,
  AgentActionRequestedEvent,
  AgentActionCompletedEvent,
  CortexSessionEventEvent,
] as const
